import { useEffect, useState, useRef, JSX, ReactElement } from 'react';

import classnames from 'classnames';

import { injectModule, InjectModule } from '../../showcase-inject';
import { createElement } from '../utils';

export type ModuleState = 'success' | 'error' | 'unmount';

export interface IncludeParams {
  nowInRuntime: string | null;
  activedModulesList: string[];
  modulesState: { name: string; state: ModuleState }[];
}

export interface InjectedModuleProps {
  name: string;
  host: string;
  mode?: ShadowRootMode;
  fallback?: JSX.Element;
  virtual?: {
    dom?: boolean;
    window?: boolean;
  };
  className?: string;
  containerStyle?: { [key: string]: string | number };
  before?: (file: string | undefined) => string;
  onPreload?: () => void;
  onLoad?: () => void;
  onGetContext?: (ctx: IncludeParams) => void;
  onError?: (
    error: string | boolean,
    errors: { [key: number]: string },
  ) => void;
  errorElement?: (error: string | boolean, rerender: () => void) => JSX.Element;
  props?: { [key: string]: any };
}

/**
 * @description The component is responsible for connecting the module (worker)
 * @param props
 * @param props.name module name
 * @param props.host module host
 * @param props.fallback preloader element
 * @param props.onPreload on preload callback
 * @param props.before before callback
 * @param props.virtual virtual module
 * @param props.react is react
 * @param props.onGetContext context handler
 * @param props.errorElement error element
 * @param props.onError error handler
 * @param props.mode shadow root mode
 * @return getModule, render, unmount, beforeCallback, getContext
 */
export function InjectedModule({
  name,
  host,
  fallback,
  onPreload,
  onLoad,
  before,
  onGetContext,
  className,
  containerStyle,
  virtual,
  errorElement,
  onError,
  mode = 'closed',
  props,
}: InjectedModuleProps): ReactElement {
  const [load, setLoad] = useState(false);
  const [error, setError] = useState<boolean | string>(false);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const moduleRef = useRef<InjectModule | null>(null);

  const handleRefresh = async (): Promise<void> => {
    setLoad(false);
    setError(false);
    await moduleRef.current?.renderModule(() => {
      onLoad?.();
      setLoad(false);
      onGetContext?.(moduleRef.current!.getContext());
      setError(false);
      setLoad(true);
    });
  };

  /**
   * @description Data processing and module rendering
   * @return context
   */

  const handleCreateModule = (): void => {
    // injected module
    const target = mainContainerRef.current?.attachShadow({
      mode,
    });
    const element = createElement(name, target, containerStyle);

    moduleRef.current = injectModule({
      element,
      host,
      name,
      virtual,
      props,
      mainContainerRef,
    });

    moduleRef.current.errorBoundary((err, errors) => {
      onError?.(error, errors);
      setError(err);
      setLoad(true);
    });

    // preload callback
    onPreload?.();

    // Before  callback
    if (before) {
      moduleRef.current.beforeCallback(before);
    }

    // render module
    (async function () {
      await handleRefresh();
    })();
  };

  // Starting the handler after mounting the module
  useEffect(() => {
    if (!mainContainerRef.current) {
      return;
    }

    handleCreateModule();
    return () => {
      moduleRef.current?.unmount(() => {
        setLoad(false);
        setError(false);
      });
    };
  }, []);

  // renders the component in the shadow root
  return (
    <>
      {!load && (fallback || <b>Loading...</b>)}
      <div
        className={classnames(className, {
          'mf-loading': !load,
          'mf-error': error,
        })}
        ref={mainContainerRef}
        data-testid={`mf-wrap-${name}`}
      />
      {error &&
        (errorElement ? (
          errorElement(error, handleRefresh)
        ) : (
          <p>Internal error of the module</p>
        ))}
    </>
  );
}
